export type WorkflowStatusKey =
    'patrolSetupRequired'
    | 'patrolScheduled'
    | 'patrolRunning'
    | 'patrolCompleted'
    | 'aiRequired'
    | 'recaptureRequested'
    | 'fieldCheckRequired'
    | 'reportRequired'
    | 'reportGenerated';

export interface WorkflowStatusDefinition {
    label: string;
    className: string;
    cssClass: string;
    priority: number;
}

const ACTIVE_BADGE_CLASS = 'border-white/30 bg-white/15 text-white';

export const WORKFLOW_STATUS: Record<WorkflowStatusKey, WorkflowStatusDefinition> = {
    patrolSetupRequired: {
        label: '순찰 설정 필요',
        className: 'border-amber-200 bg-amber-50 text-amber-800',
        cssClass: 'is-work-patrol-setup-required',
        priority: 5
    },
    patrolScheduled: {
        label: '순찰 예정',
        className: 'border-zinc-200 bg-zinc-50 text-zinc-700',
        cssClass: 'is-work-patrol-scheduled',
        priority: 10
    },
    patrolRunning: {
        label: '순찰 중',
        className: 'border-sky-200 bg-sky-50 text-sky-700',
        cssClass: 'is-work-patrol-running',
        priority: 20
    },
    patrolCompleted: {
        label: '순찰 완료',
        className: 'border-teal-200 bg-teal-50 text-teal-700',
        cssClass: 'is-work-patrol-completed',
        priority: 30
    },
    aiRequired: {
        label: 'AI 확인 필요',
        className: 'border-blue-200 bg-blue-50 text-blue-700',
        cssClass: 'is-work-ai-required',
        priority: 40
    },
    recaptureRequested: {
        label: '재촬영 요청',
        className: 'border-amber-200 bg-amber-50 text-amber-800',
        cssClass: 'is-work-recapture-requested',
        priority: 50
    },
    fieldCheckRequired: {
        label: '현장 확인 필요',
        className: 'border-rose-200 bg-rose-50 text-rose-700',
        cssClass: 'is-work-field-check-required',
        priority: 70
    },
    reportRequired: {
        label: '보고서 작성 필요',
        className: 'border-cyan-200 bg-cyan-50 text-cyan-800',
        cssClass: 'is-work-report-required',
        priority: 35
    },
    reportGenerated: {
        label: '보고서 생성 완료',
        className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        cssClass: 'is-work-report-generated',
        priority: 30
    }
};

export function normalizeWorkflowStatusKey(value: any): WorkflowStatusKey {
    const text = String(value || '').trim();
    const normalized = text.toLowerCase().replace(/[\s_-]+/g, '');
    const upper = text.toUpperCase();

    if ([
        'setup',
        'draft',
        'needsetup',
        'needscheck',
        'patrolsetuprequired'
    ].indexOf(normalized) >= 0 || ['DRAFT', 'NEEDS_CHECK'].indexOf(upper) >= 0 || text.indexOf('설정 필요') >= 0 || text.indexOf('등록 필요') >= 0) {
        return 'patrolSetupRequired';
    }

    if ([
        'assigned',
        'scheduled',
        'ready',
        'pending',
        'patrolscheduled',
        'unstarted',
        'notdeployed',
        'undeployed'
    ].indexOf(normalized) >= 0 || ['UNDEPLOYED', 'NOT_DEPLOYED', 'READY'].indexOf(upper) >= 0 || text.indexOf('순찰 예정') >= 0 || text.indexOf('배정') >= 0 || text.indexOf('시작 전') >= 0) {
        return 'patrolScheduled';
    }

    if ([
        'patrol',
        'running',
        'capture',
        'capturing',
        'upload',
        'uploading',
        'drone',
        'preflight',
        'patrolrunning'
    ].indexOf(normalized) >= 0 || text.indexOf('순찰 중') >= 0 || text.indexOf('진행 중') >= 0 || text.indexOf('촬영 중') >= 0 || text.indexOf('업로드 중') >= 0 || text.indexOf('이륙 중') >= 0 || text.indexOf('복귀 중') >= 0) {
        return 'patrolRunning';
    }

    if ([
        'patrolcompleted',
        'patrolcomplete',
        'missioncompleted',
        'missioncomplete',
        'completedpatrol'
    ].indexOf(normalized) >= 0 || text.indexOf('순찰 완료') >= 0) {
        return 'patrolCompleted';
    }

    if ([
        'ai',
        'underreview',
        'airequired',
        'aicheckrequired',
        'reviewrequired',
        'review'
    ].indexOf(normalized) >= 0 || upper === 'UNDER_REVIEW' || text.indexOf('AI 확인 필요') >= 0 || text.indexOf('AI 확인 대기') >= 0 || text.indexOf('확인 대기') >= 0 || text.indexOf('담당자 확인') >= 0) {
        return 'aiRequired';
    }

    if ([
        'recapture',
        'retake',
        'recapturerequested',
        'needsretake'
    ].indexOf(normalized) >= 0 || upper === 'RECAPTURE_REQUESTED' || text.indexOf('재촬영') >= 0 || text.indexOf('재확인') >= 0) {
        return 'recaptureRequested';
    }

    if ([
        'field',
        'fieldcheck',
        'fieldcheckrequired'
    ].indexOf(normalized) >= 0 || upper === 'FIELD_CHECK_REQUIRED' || text.indexOf('현장 확인') >= 0) {
        return 'fieldCheckRequired';
    }

    if ([
        'report',
        'reportrequired',
        'reportready',
        'closed',
        'falsepositive'
    ].indexOf(normalized) >= 0 || ['CLOSED', 'FALSE_POSITIVE'].indexOf(upper) >= 0 || text.indexOf('보고서 작성 필요') >= 0 || text.indexOf('보고 준비') >= 0 || text.indexOf('생성 전') >= 0 || text.indexOf('AI 확인 결과 반영') >= 0 || text.indexOf('이상 없음') >= 0) {
        return 'reportRequired';
    }

    if ([
        'generated',
        'reportgenerated',
        'completed'
    ].indexOf(normalized) >= 0 || text.indexOf('보고서 생성 완료') >= 0 || text.indexOf('생성 완료') >= 0) {
        return 'reportGenerated';
    }

    return 'patrolScheduled';
}

export function workflowStatus(key: any): WorkflowStatusDefinition {
    return WORKFLOW_STATUS[normalizeWorkflowStatusKey(key)];
}

export function workflowStatusLabel(key: any): string {
    return workflowStatus(key).label;
}

export function workflowStatusClass(key: any, active: boolean = false): string {
    return active ? ACTIVE_BADGE_CLASS : workflowStatus(key).className;
}

export function workflowStatusCssClass(key: any): string {
    return workflowStatus(key).cssClass;
}
